import { useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, put } from '../api/client';
import { isMissing } from '../api/queries';
import type { AutoChargeSettings } from '../api/types';
import { useT } from '../i18n';
import { useLive } from '../live/store';
import { AUTO_CHARGE_DEFAULTS, occLabel, settingsKey, useChargers, useChargeView, useRobotSettings } from '../lib/charge';
import { usePopoverClose } from '../lib/hooks';
import { toast, toastErr } from './toast';

function Slider({ label, value, min, max, onChange, hint }: { label: string; value: number; min: number; max: number; onChange: (v: number) => void; hint?: string }) {
  return (
    <label className="charge-slider">
      <span className="row" style={{ justifyContent: 'space-between' }}>
        <span>{label}</span>
        <span className="mono">{value}%</span>
      </span>
      <input type="range" min={min} max={max} step={1} value={value} onChange={(e) => onChange(Number(e.target.value))} aria-valuetext={`${value}%`} />
      {hint && <span className="muted" style={{ fontSize: 11 }}>{hint}</span>}
    </label>
  );
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** §21 Auto-charge card (Robot page, right column). Saves via PUT /api/robots/:serial/settings {autoCharge}. */
export function AutoChargeCard({ serial }: { serial: string }) {
  const t = useT();
  const qc = useQueryClient();
  const q = useRobotSettings(serial);
  const st = useLive((s) => s.robots[serial]?.status);
  const view = useChargeView(serial);
  const charger = useChargers(st?.currentMap);
  const [draft, setDraft] = useState<AutoChargeSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  const server = q.data?.autoCharge ?? AUTO_CHARGE_DEFAULTS;
  const v = draft ?? server;
  const apiMissing = missing || isMissing(q.error);
  const edit = (patch: Partial<AutoChargeSettings>) => {
    const n = { ...v, ...patch };
    // keep critical < low < resume
    n.lowPercent = clamp(n.lowPercent, 5, 50);
    n.resumePercent = clamp(Math.max(n.resumePercent, n.lowPercent + 5), 50, 100);
    n.criticalPercent = clamp(Math.min(n.criticalPercent, n.lowPercent - 1), 5, 20);
    setDraft(n);
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = await put<{ autoCharge?: Partial<AutoChargeSettings> } | null>(`/api/robots/${encodeURIComponent(serial)}/settings`, { autoCharge: v });
      qc.setQueryData(settingsKey(serial), { autoCharge: { ...v, ...(r?.autoCharge ?? {}) } });
      setDraft(null);
      toast(t('charge.saved'), 'ok');
    } catch (e) {
      if (e instanceof ApiError && isMissing(e)) setMissing(true);
      toastErr(e, t('charge.saveFailed'));
    } finally {
      setBusy(false);
    }
  };
  const state = st?.autoCharge?.state ?? (view ? (view === 'charging' ? 'charging' : view) : null);
  return (
    <section className="panel charge-card" aria-label={t('charge.title')}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="h2">⚡ {t('charge.title')}</h2>
        {state && <span className={`charge-state s-${state}`}>{t(`charge.state.${['idle', 'going', 'charging', 'failed'].includes(String(state)) ? state : 'idle'}` as 'charge.state.idle')}</span>}
        <button
          type="button"
          role="switch"
          aria-checked={v.enabled}
          aria-label={t('charge.enabled')}
          className={`toggle${v.enabled ? ' on' : ''}`}
          disabled={apiMissing || q.isLoading}
          onClick={() => edit({ enabled: !v.enabled })}
        >
          <span />
        </button>
      </div>
      {apiMissing ? (
        <span className="muted" style={{ fontSize: 12 }}>
          {t('charge.apiMissing')}
        </span>
      ) : (
        <fieldset className="col" style={{ gap: 10, border: 0, padding: 0, margin: 0, opacity: v.enabled ? 1 : 0.55 }} disabled={!v.enabled || q.isLoading}>
          <Slider label={t('charge.low')} value={v.lowPercent} min={5} max={50} onChange={(n) => edit({ lowPercent: n })} />
          <Slider label={t('charge.resume')} value={v.resumePercent} min={50} max={100} onChange={(n) => edit({ resumePercent: n })} />
          <Slider label={t('charge.critical')} value={v.criticalPercent} min={5} max={20} onChange={(n) => edit({ criticalPercent: n })} hint={t('charge.criticalHint')} />
          <label className="row" style={{ gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={v.resumeTask} onChange={(e) => edit({ resumeTask: e.target.checked })} />
            {t('charge.resumeTask')}
          </label>
        </fieldset>
      )}
      {charger.known && !charger.chargers.length && (
        <div className="charge-warn" role="note">
          <span>⚠ {t('charge.noCharger', { map: st?.currentMap ?? '' })}</span>
          {charger.mapId && (
            <Link className="btn xs" to={`/maps/${charger.mapId}?charger=1`}>
              {t('charge.openMap')}
            </Link>
          )}
        </div>
      )}
      {charger.chargers.length > 0 && (
        <div className="row" style={{ justifyContent: 'space-between', fontSize: 12 }}>
          <span className="muted">⚡ {t('charge.countOnMap', { n: charger.chargers.length })}</span>
          {charger.mapId && (
            <Link className="btn xs ghost" to={`/maps/${charger.mapId}`}>
              {t('charge.showOnMap')}
            </Link>
          )}
        </div>
      )}
      {draft && (
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className="btn sm primary grow" disabled={busy} onClick={() => void save()}>
            {busy ? <span className="spinner sm" aria-hidden /> : t('charge.save')}
          </button>
          <button type="button" className="btn sm ghost" disabled={busy} onClick={() => setDraft(null)}>
            {t('common.cancel')}
          </button>
        </div>
      )}
    </section>
  );
}

/** Banner over the camera while auto-charge drives to the station, or after it failed. */
export function ChargeBanner({ serial }: { serial: string }) {
  const t = useT();
  const view = useChargeView(serial);
  const ev = useLive((s) => s.autoCharge[serial]);
  const battery = useLive((s) => s.robots[serial]?.status?.battery);
  const b = Math.round(typeof ev?.battery === 'number' ? ev.battery : (battery ?? 0));
  if (view === 'going')
    return (
      <div className="stuck-banner charge" role="status">
        ⚡ {t('charge.lowToast', { b })}
      </div>
    );
  if (view === 'failed')
    return (
      <div className="stuck-banner failed" role="alert">
        <b>⛔ {t('charge.failedToast')}</b>
        {ev?.reason && <span className="stuck-sub">{ev.reason}</span>}
      </div>
    );
  return null;
}

/**
 * Caret menu next to a "Go to charger" button (§21.x): "Nearest free" (dock without pointId) + each ⚡ point of the
 * robot's current map with its occupancy (dock {pointId}).
 */
export function DockMenu({ serial, mapName, onDock, disabled, className = '' }: { serial: string; mapName: string | null | undefined; onDock: (pointId?: string) => void; disabled?: boolean; className?: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = usePopoverClose(open, () => setOpen(false));
  const robots = useLive((s) => s.robots);
  const { chargers } = useChargers(mapName);
  const nameOf = (s: string) => robots[s]?.name ?? s;
  const pick = (pointId?: string) => {
    setOpen(false);
    onDock(pointId);
  };
  return (
    <span className="pop-anchor dock-caret-wrap">
      <button type="button" className={`btn dock-caret ${className}`} aria-haspopup="menu" aria-expanded={open} aria-label={t('charge.chooseCharger')} title={t('charge.chooseCharger')} disabled={disabled} onClick={() => setOpen(!open)}>
        ▾
      </button>
      {open && (
        <div ref={ref} className="popover dock-menu" role="menu" style={{ right: 0, bottom: 44 }}>
          <button type="button" role="menuitem" className="menu-item primary" onClick={() => pick()}>
            ⚡ {t('charge.nearestFree')}
          </button>
          {chargers.map(({ point, occ }) => (
            <button key={point.id} type="button" role="menuitem" className="menu-item dock-item" onClick={() => pick(point.id)} title={occLabel(occ, nameOf)}>
              <span className={`occ-dot s-${occ.state}`} aria-hidden />
              <span className="grow ellipsis">{point.name}</span>
              <span className={`occ-text s-${occ.state}`}>{occ.robot === serial && occ.state !== 'free' ? t('charge.occ.thisRobot') : occLabel(occ, nameOf)}</span>
            </button>
          ))}
          {!chargers.length && <span className="muted" style={{ fontSize: 12, padding: '6px 10px' }}>{t('charge.noChargersShort')}</span>}
        </div>
      )}
    </span>
  );
}
